import { memo, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import {
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Tab,
  TabList,
  TabPanel,
} from "@mui/joy";

import { ExportIcon } from "@zenith/assets/icons/components";
import Info from "@zenith/assets/icons/Info";
import {
  DEFAULT_CURRENCY_SYMBOL,
  EXPORT_ERROR_MODAL_BODY,
  EXPORT_ERROR_MODAL_TITLE,
  LEDGER_BALANCE_LABEL,
  TRANSACTIONS_LIMIT_NUMBER,
} from "@zenith/data/constants";
import {
  ButtonComponent,
  ModalComponent,
  TabsComponent,
} from "@zenith/components";
import {
  ApiResponseDTO,
  WalletApiService,
  WalletBalanceInfoResponseDTO,
} from "@zenith/services/user";
import { formatCurrency } from "@zenith/utils";
import { useTransactionsContext, useUserDetailsContext } from "@zenith/context";
import { TransactionsContextType } from "@zenith/context/global-context/types";
import {
  OperationsTableComponent,
  TransactionsTableComponent,
} from "@zenith/features/Transactions/components";
import { UserRole } from "@zenith/types";
import { usePermissions } from "@zenith/features/AuthZ/usePermissions";
import {
  TransactionsParametersExportRecord,
  TransactionsParametersRecord,
} from "@zenith/features/Transactions/interfaces";
import { OperationFullDTO } from "@zenith/services/transactions";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useBalanceVisibility } from "@zenith/hooks/useBalanceVisibility";
import { useOutletView } from "@zenith/features/OutletManagement/hooks/useOutletView";

/* eslint-disable-next-line */
export interface TransactionsPageProps {}
interface FiltersData {
  maximumAmount: number | undefined;
  minimumAmount: number | undefined;
  paymentMethods: TransactionsParametersRecord["paymentMethods"];
  transactionDateEnd: string | undefined;
  transactionDateStart: string | undefined;
  status: OperationFullDTO.status[];
}

const TransactionsPage = memo(function TransactionsPage(
  props: TransactionsPageProps
) {
  const location = useLocation();
  const {
    transactionsContext,
    setTransactionsContext,
    clearTransactionsContext,
  } = useTransactionsContext();
  const { hasTransactions, totalNoOfTransactions, isOperation } =
    transactionsContext;
  const { userDetailsContext } = useUserDetailsContext();
  // BOAS-7641: every role has a Transactions section, scoped by the server to
  // their outlet or to the transactions they collected, and every one of them
  // may export it. The role that applies is the profile in use, not the claim
  // the token was minted with.
  const { role: profileRole, resolved, can } = usePermissions();
  const isAdmin = profileRole === "admin";
  const canViewTransactions = can("transactions:view");
  const showsBalance = resolved && can("balance:view");

  // Use the custom hook for balance visibility
  const { isBalanceVisible, toggleBalanceVisibility } = useBalanceVisibility();

  const {
    isSingleOutletView,
    isAllOutletsSelected,
    selectedOutlet,
    outlets,
    isLoadingBalances,
  } = useOutletView();

  const [walletInfo, setWalletInfo] = useState<WalletBalanceInfoResponseDTO>();
  const [isLoadingWalletInfo, setIsLoadingWalletInfo] =
    useState<boolean>(isAdmin);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(
    localStorage.getItem("activeTab") || "transactions"
  );
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [navigationKey, setNavigationKey] = useState<string>(
    location.key || "initial"
  );
  const navState = location.state || {};

  const [filters, setFilters] = useState<any>(
    navState.filters || {
      maximumAmount: undefined,
      minimumAmount: undefined,
      paymentMethods: [],
      transactionDateEnd: undefined,
      transactionDateStart: undefined,
      status: [],
    }
  );

  const initialFilters: FiltersData = {
    maximumAmount: undefined,
    minimumAmount: undefined,
    paymentMethods: [],
    transactionDateEnd: undefined,
    transactionDateStart: undefined,
    status: [],
  };

  const [searchText, setSearchText] = useState(navState.searchText || "");
  const [currentPage, setCurrentPage] = useState(navState.currentPage || 1);
  const [direction, setDirection] = useState(navState.direction ?? false);
  const [ledgerTooltipOpen, setLedgerTooltipOpen] = useState(false);

  const showTransactionsTab = canViewTransactions;

  const dashboardTooltipProps = {
    componentsProps: {
      tooltip: {
        sx: {
          backgroundColor: "#373737",
          borderRadius: "8px",
          fontSize: "10px",
          color: "white",
        },
      },
      arrow: {
        sx: {
          color: "#373737",
        },
      },
    },
  };

  const formatHiddenBalance = (): string => {
    return "••••••••";
  };

  useEffect(() => {
    const initializeTransactionsPage = (newTab: string) => {
      const defaultTab = newTab ? newTab : "transactions";
      setActiveTab(defaultTab);
      clearTransactionsContext();

      const newContext: TransactionsContextType = {
        isOperation: false,
        hasTransactions: false,
        totalNoOfTransactions: 0,
        accountOwner: "",
        amount: 0,
        bankId: "",
        bankName: "",
        beneficiaryAccountNumber: "",
        beneficiaryName: "",
        description: "",
        transactionId: "",
        isExport: false,
        mediaType: undefined,
        status: undefined,
        totalAmount: 0,
        transactionDate: undefined,
        operationId: undefined,
        refundedOperationId: undefined,
        errorMessage: undefined,
        paymentMethod: undefined,
        canBeRefunded: false,
        refundedAmount: 0,
        operationCode: undefined,
        terminalId: undefined,
        disputeId: undefined,
        nameEnquiryRef: undefined,
        bankCode: undefined,
        narration: undefined,
        sessionId: undefined,
        feeValue: 0,
        refundReason: undefined,
      };
      setTransactionsContext(newContext);
      localStorage.setItem("activeTab", defaultTab);
      setNavigationKey(location.key || "initial");
    };

    initializeTransactionsPage(activeTab);
  }, [location.key, clearTransactionsContext, isAdmin, setTransactionsContext]);

  // Handle tab changes
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    clearTransactionsContext();
    setFilters(initialFilters);
    setSearchText("");
    setCurrentPage(1);
    setDirection(false);
    const newContext: TransactionsContextType = {
      isOperation: newTab ? newTab === "operations" : false,
      hasTransactions: false,
      totalNoOfTransactions: 0,
      accountOwner: "",
      amount: 0,
      bankId: "",
      bankName: "",
      beneficiaryAccountNumber: "",
      beneficiaryName: "",
      description: "",
      transactionId: "",
      isExport: false,
      mediaType: undefined,
      status: undefined,
      totalAmount: 0,
      transactionDate: undefined,
      operationId: undefined,
      refundedOperationId: undefined,
      errorMessage: undefined,
      paymentMethod: undefined,
      canBeRefunded: false,
      refundedAmount: 0,
      operationCode: undefined,
      terminalId: undefined,
      disputeId: undefined,
      nameEnquiryRef: undefined,
      bankCode: undefined,
      narration: undefined,
      sessionId: undefined,
      feeValue: 0,
      refundReason: undefined,
    };
    setTransactionsContext(newContext);
    localStorage.setItem("activeTab", newTab);
  };

  const tabDefaultValue = useMemo(() => {
    return activeTab === "transactions" ? 0 : 1;
  }, [activeTab]);

  useEffect(() => {
    // The wallet endpoint answers for the outlet the session is acting for, so
    // a team member's summary is their own outlet's, never the business's.
    setIsLoadingWalletInfo(true);
    WalletApiService.getWalletDetails()
      .then((response: WalletBalanceInfoResponseDTO | ApiResponseDTO) => {
        if ("data" in response && response.data) {
          setWalletInfo(response.data as WalletBalanceInfoResponseDTO);
        }
      })
      .catch((error) => {
        console.error("Error fetching wallet details:", error);
      })
      .finally(() => {
        setIsLoadingWalletInfo(false);
      });
  }, []);

  const ExportErrorModal = ModalComponent;

  function handleModalClose(): void {
    setShowExportModal(false);
  }

  function handleCancelButton(): void {
    handleModalClose();
  }

  function handleExportButton(): void {
    const newContext: TransactionsContextType = {
      ...transactionsContext,
      isExport: true,
    };
    setTransactionsContext(newContext);
    handleModalClose();
  }

  function handlePdfButton(): void {
    if ((totalNoOfTransactions as number) >= TRANSACTIONS_LIMIT_NUMBER) {
      const newContext: TransactionsContextType = {
        ...transactionsContext,
        mediaType: TransactionsParametersExportRecord.mediaType.PDF,
      };
      setTransactionsContext(newContext);
      setShowExportModal(true);
    } else {
      const newContext: TransactionsContextType = {
        ...transactionsContext,
        isExport: true,
        mediaType: TransactionsParametersExportRecord.mediaType.PDF,
      };
      setTransactionsContext(newContext);
      handleModalClose();
    }
  }

  function handleCsvButton(): void {
    if ((totalNoOfTransactions as number) >= TRANSACTIONS_LIMIT_NUMBER) {
      const newContext: TransactionsContextType = {
        ...transactionsContext,
        mediaType: TransactionsParametersExportRecord.mediaType.CSV,
      };
      setTransactionsContext(newContext);
      setShowExportModal(true);
    } else {
      const newContext: TransactionsContextType = {
        ...transactionsContext,
        isExport: true,
        mediaType: TransactionsParametersExportRecord.mediaType.CSV,
      };
      setTransactionsContext(newContext);
      handleModalClose();
    }
  }

  // The balance belongs to the outlet in view, and the session wallet is only
  // the fallback: after a switch the wallet call answered for the outlet the
  // server was signed into at the time, so the figure sat stale until another
  // screen refetched it.
  const viewBalance = (() => {
    if (isLoadingBalances) return null;
    if (isSingleOutletView && selectedOutlet) {
      return {
        availableBalance:
          selectedOutlet.availableBalance ?? selectedOutlet.balance ?? 0,
        ledgerBalance:
          selectedOutlet.ledgerBalance ?? selectedOutlet.balance ?? 0,
      };
    }
    if (isAllOutletsSelected && outlets.length) {
      return {
        availableBalance: outlets.reduce(
          (total, outlet) =>
            total + (outlet.availableBalance ?? outlet.balance ?? 0),
          0
        ),
        ledgerBalance: outlets.reduce(
          (total, outlet) =>
            total + (outlet.ledgerBalance ?? outlet.balance ?? 0),
          0
        ),
      };
    }
    return null;
  })();

  const displayedAvailable =
    viewBalance?.availableBalance ??
    Number(walletInfo?.balance?.availableBalance || 0);
  const displayedLedger =
    viewBalance?.ledgerBalance ?? walletInfo?.balance?.ledgerBalance;

  const pendingBalance = walletInfo?.balance?.pendingBalance || 0;
  const isLoadingOperations = isLoadingWalletInfo;

  return (
    <div key={`transactions-page-${location.key}-${activeTab}`}>
      <ExportErrorModal
        title={EXPORT_ERROR_MODAL_TITLE}
        body={EXPORT_ERROR_MODAL_BODY}
        showModal={showExportModal}
        onClickButton={handleModalClose}
        onClickClose={handleModalClose}
        doubleOptions
        leftButtonText="Cancel"
        rightButtonText="Export"
        onLeftButtonClick={handleCancelButton}
        onRightButtonClick={handleExportButton}
        showErrorIcon={false}
      />
      <div className="flex flex-wrap justify-between gap-5">
        {isLoadingWalletInfo ? (
          <div className="ml-40 flex text-center">
            <CircularProgress className={"text-[#F3B8B3]"} size={"3rem"} />
          </div>
        ) : (
          <div className="flex md:mr-10">
            {showsBalance && walletInfo?.balance && (
              <>
                <div className="pr-10 md:pr-16">
                  <div className="flex flex-row items-center gap-2">
                    <p className="pb-2 text-sm text-neutral-400 md:text-base">
                      Available balance
                    </p>
                    <IconButton
                      onClick={toggleBalanceVisibility}
                      className="p-0"
                      size="small"
                    >
                      {isBalanceVisible ? (
                        <Visibility
                          className="text-neutral-400"
                          fontSize="small"
                        />
                      ) : (
                        <VisibilityOff
                          className="text-neutral-400"
                          fontSize="small"
                        />
                      )}
                    </IconButton>
                  </div>
                  <p className="text-nowrap text-2xl text-neutral-900 md:text-4.5xl">
                    <span className="text-xl text-neutral-400 md:text-3.5xl">
                      {DEFAULT_CURRENCY_SYMBOL}
                    </span>{" "}
                    {isBalanceVisible
                      ? formatCurrency(Number(displayedAvailable || 0), false)
                      : formatHiddenBalance()}
                  </p>
                </div>
                {displayedLedger !== undefined && displayedLedger > 0 && (
                  <div className="border-l border-neutral-100 pl-10 md:pl-16">
                    <div className="flex items-center gap-1 pb-2">
                      <p className="text-sm text-neutral-400 md:text-base">
                        Ledger balance
                      </p>
                      {walletInfo?.balance?.ledgerBalanceTooltip && (
                        <ClickAwayListener
                          onClickAway={() => setLedgerTooltipOpen(false)}
                        >
                          <div>
                            <Tooltip
                              {...dashboardTooltipProps}
                              placement="top"
                              arrow
                              disableHoverListener
                              title={
                                <div>
                                  <p
                                    style={{
                                      fontWeight: 600,
                                      fontSize: 13,
                                      marginBottom: 6,
                                    }}
                                  >
                                    {LEDGER_BALANCE_LABEL}
                                  </p>
                                  <p
                                    style={{
                                      fontWeight: 400,
                                      fontSize: 11,
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {walletInfo.balance.ledgerBalanceTooltip}
                                  </p>
                                </div>
                              }
                              onClose={() => setLedgerTooltipOpen(false)}
                              open={ledgerTooltipOpen}
                            >
                              <IconButton
                                onClick={() => setLedgerTooltipOpen(true)}
                                className="p-0 text-neutral-400"
                                size="small"
                              >
                                <Info />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </ClickAwayListener>
                      )}
                    </div>
                    <p className="text-nowrap text-2xl text-neutral-900 md:text-4.5xl">
                      <span className="text-xl text-neutral-400 md:text-3.5xl">
                        {DEFAULT_CURRENCY_SYMBOL}
                      </span>{" "}
                      {isBalanceVisible
                        ? formatCurrency(Number(displayedLedger || 0), false)
                        : formatHiddenBalance()}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {canViewTransactions && (
          <Dropdown>
            <MenuButton className={`hover: border-none hover:bg-transparent`}>
              <ButtonComponent
                label="Export"
                id="continue-button"
                size="large"
                type="secondary"
                disabled={!hasTransactions}
                className="px-24"
                iconStart={<ExportIcon fill={"currentColor"} />}
              />
            </MenuButton>

            <Menu>
              <MenuItem onClick={handleCsvButton}>Export CSV</MenuItem>
              <MenuItem onClick={handlePdfButton}>Export PDF</MenuItem>
            </Menu>
          </Dropdown>
        )}
      </div>
      <div className="mt-12">
        <TabsComponent
          value={tabDefaultValue}
          defaultValue={tabDefaultValue}
          aria-label="transactions-tabs"
          onChange={(_, value) => {
            const newTab = value === 0 ? "transactions" : "operations";
            handleTabChange(newTab);
          }}
        >
          <TabList>
            <Tab
              value={0}
              onMouseEnter={() => setHoveredTab("transactions")}
              onMouseLeave={() => setHoveredTab(null)}
              sx={{
                borderBottom:
                  activeTab === "transactions"
                    ? ""
                    : hoveredTab === "transactions"
                    ? "1px solid 200"
                    : "2px solid transparent",
              }}
            >
              Transactions
            </Tab>
            <Tab
              value={1}
              onMouseEnter={() => setHoveredTab("operations")}
              onMouseLeave={() => setHoveredTab(null)}
              sx={{
                borderBottom:
                  activeTab === "operations"
                    ? ""
                    : hoveredTab === "operations"
                    ? "2px solid 200"
                    : "2px solid transparent",
              }}
            >
              Operations
            </Tab>
          </TabList>
          <TabPanel value={0}>
            <TransactionsTableComponent
              key="transactions"
              filters={filters}
              setFilters={setFilters}
              searchText={searchText}
              setSearchText={setSearchText}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              direction={direction}
              setDirection={setDirection}
            />
          </TabPanel>
          <TabPanel value={1}>
            <OperationsTableComponent
              key="operations"
              filters={filters}
              setFilters={setFilters}
              searchText={searchText}
              setSearchText={setSearchText}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              direction={direction}
              setDirection={setDirection}
              pendingBalance={pendingBalance}
              isLoadingOperations={isLoadingOperations}
            />
          </TabPanel>
        </TabsComponent>
      </div>
    </div>
  );
});

export default TransactionsPage;
